import Ionicons from '@expo/vector-icons/Ionicons'
import { Link, useRouter } from 'expo-router'
import type { Href } from 'expo-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Animated, Modal, Platform, Pressable, View, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Icon } from '@/components/ui/Icon'
import { Text } from '@/components/ui/Text'
import { useAuth } from '@/contexts/AuthContext'
import { useI18n } from '@/contexts/I18nContext'
import { useTheme, useThemeColors, type ThemeMode } from '@/contexts/ThemeContext'
import { useNotificationCount } from '@/hooks/useNotificationCount'
import { type SupportedLanguage } from '@/lib/i18n'
import TurbinaLogo from './TurbinaLogo'

const LANGUAGES: { code: SupportedLanguage }[] = [{ code: 'en' }, { code: 'es' }, { code: 'pt' }]

const THEME_OPTIONS: { mode: ThemeMode; iconName: 'sun-o' | 'moon-o' | 'cog' }[] = [
  { mode: 'light', iconName: 'sun-o' },
  { mode: 'dark', iconName: 'moon-o' },
  { mode: 'system', iconName: 'cog' },
]

type NavItem = { label: string; route: string }

type Props = {
  activeRoute?: string
  userName?: string
  scrollOffset?: number
  savingsProgress?: number
  transparent?: boolean
  notificationCount?: number
  onNotificationPress?: () => void
  fixed?: boolean
}

// eslint-disable-next-line max-params
function buildNavItems(
  isAuthenticated: boolean,
  isApprovedMember: boolean,
  isAdmin: boolean,
  t: (k: string) => string,
  includeSideMenuLinks: boolean = false
): NavItem[] {
  const publicLinks = [
    { label: t('publicHeader.whatWeDo'), route: '/about' },
    { label: t('publicHeader.help'), route: '/faq' },
  ]
  const items: NavItem[] = isApprovedMember
    ? [
        { label: t('header.myPlan'), route: '/dashboard' },
        { label: t('header.community'), route: '/community' },
        { label: t('header.campaign'), route: '/campaign' },
        // "What we do" and "Help" are only shown in the side menu, not the top header
        ...(includeSideMenuLinks ? publicLinks : []),
      ]
    : isAuthenticated
      ? publicLinks
      : [...publicLinks, { label: t('publicHeader.login'), route: '/auth' }]
  if (isAdmin) items.push({ label: t('header.hq'), route: '/hq' })
  return items
}

type SettingsContentProps = {
  isApprovedMember: boolean
  isAuthenticated: boolean
  themeMode: ThemeMode
  setThemeMode: (m: ThemeMode) => void
  locale: SupportedLanguage
  changeLanguage: (l: SupportedLanguage) => void
  onSignOut: () => void
  onClose: () => void
  navItems?: NavItem[]
  activeRoute?: string
  onNavigate?: (route: string) => void
  showNav?: boolean
}

function SettingsContent({
  isApprovedMember,
  isAuthenticated,
  themeMode,
  setThemeMode,
  locale,
  changeLanguage,
  onSignOut,
  onClose,
  navItems,
  activeRoute,
  onNavigate,
  showNav,
}: SettingsContentProps) {
  const { t } = useTranslation()
  const colors = useThemeColors()
  const router = useRouter()

  return (
    <View className="gap-4 rounded-3xl p-5" style={{ backgroundColor: colors.surface }}>
      <View className="flex-row items-center justify-between">
        <Text className="font-medium text-base" style={{ color: colors.dark }} />
        <Pressable
          onPress={onClose}
          className="h-7 w-7 items-center justify-center rounded-full"
          style={{ backgroundColor: colors.border }}
        >
          <Icon name="times" size={12} color={colors.dark} />
        </Pressable>
      </View>

      {showNav && navItems && navItems.length > 0 && (
        <View className="gap-2">
          <Text
            className="text-[11px] font-semibold tracking-[1.6px] uppercase"
            style={{ color: colors.muted }}
          >
            {t('header.navigate')}
          </Text>
          <View className="gap-1">
            {navItems.map((item) => {
              const isActive = activeRoute === item.route
              return (
                <Pressable
                  key={item.label}
                  className="rounded-2xl px-3 py-2 flex-row items-center"
                  style={{
                    backgroundColor: isActive ? `${colors.primary}22` : colors.background,
                  }}
                  onPress={() => {
                    onNavigate?.(item.route)
                    onClose()
                  }}
                >
                  <Text
                    className="text-xs font-semibold uppercase tracking-[1.4px]"
                    style={{ color: isActive ? colors.primary : colors.dark }}
                  >
                    {item.label}
                  </Text>
                </Pressable>
              )
            })}
            {isApprovedMember && (
              <Pressable
                className="rounded-2xl px-3 py-2 flex-row items-center"
                style={{ backgroundColor: colors.background }}
                onPress={() => {
                  onClose()
                  router.push('/settings/profile')
                }}
              >
                <Text
                  className="text-xs font-semibold uppercase tracking-[1.4px]"
                  style={{ color: colors.dark }}
                >
                  {t('header.editProfile')}
                </Text>
              </Pressable>
            )}
          </View>
        </View>
      )}

      {!showNav && isApprovedMember && (
        <Pressable
          className="rounded-2xl px-3 py-2 flex-row items-center"
          style={{ backgroundColor: colors.background }}
          onPress={() => {
            onClose()
            router.push('/settings/profile')
          }}
        >
          <Text
            className="text-xs font-semibold uppercase tracking-[1.4px]"
            style={{ color: colors.dark }}
          >
            {t('header.editProfile')}
          </Text>
        </Pressable>
      )}

      <View className="gap-2">
        <Text
          className="text-[11px] font-semibold tracking-[1.6px] uppercase"
          style={{ color: colors.muted }}
        >
          {t('settings.language')}
        </Text>
        <View className="flex-row gap-2">
          {LANGUAGES.map(({ code }) => {
            const isActive = locale === code
            const langCode = code.toUpperCase()
            return (
              <Pressable
                key={code}
                className="flex-1 rounded-2xl py-2 flex-col items-center justify-center gap-1"
                style={{
                  backgroundColor: isActive ? colors.primary : colors.background,
                  opacity: isActive ? 1 : 0.85,
                }}
                onPress={() => changeLanguage(code)}
              >
                <Icon name="globe" size={16} color={isActive ? colors.white : colors.dark} />
                <Text
                  className="text-[11px] font-semibold uppercase"
                  style={{ color: isActive ? colors.white : colors.dark }}
                >
                  {langCode}
                </Text>
              </Pressable>
            )
          })}
        </View>
      </View>

      <View className="gap-2">
        <Text
          className="text-[11px] font-semibold tracking-[1.6px] uppercase"
          style={{ color: colors.muted }}
        >
          {t('settings.theme')}
        </Text>
        <View className="flex-row gap-2">
          {THEME_OPTIONS.map(({ mode, iconName }) => {
            const isActive = themeMode === mode
            return (
              <Pressable
                key={mode}
                className="flex-1 rounded-2xl py-2 flex-col items-center justify-center gap-1"
                style={{
                  backgroundColor: isActive ? colors.primary : colors.background,
                  opacity: isActive ? 1 : 0.85,
                }}
                onPress={() => setThemeMode(mode)}
              >
                <Icon name={iconName} size={16} color={isActive ? colors.white : colors.dark} />
                <Text
                  className="text-[11px] font-semibold uppercase"
                  style={{ color: isActive ? colors.white : colors.dark }}
                >
                  {t(`settings.theme${mode.charAt(0).toUpperCase()}${mode.slice(1)}`)}
                </Text>
              </Pressable>
            )
          })}
        </View>
      </View>

      {isAuthenticated && (
        <Pressable className="rounded-2xl px-3 py-3 items-center" onPress={onSignOut}>
          <Text
            className="text-sm font-medium uppercase tracking-[1.4px]"
            style={{ color: '#e53e3e' }}
          >
            {t('settings.signOut')}
          </Text>
        </Pressable>
      )}
    </View>
  )
}

// eslint-disable-next-line complexity
export default function Header({
  activeRoute = '/dashboard',
  userName: _userName,
  scrollOffset = 0,
  savingsProgress: _savingsProgress = 0,
  transparent = true,
  notificationCount,
  onNotificationPress,
  fixed = true,
}: Props) {
  const router = useRouter()
  const { t } = useTranslation()
  const insets = useSafeAreaInsets()
  const { width } = useWindowDimensions()
  const isDesktop = width >= 1024
  const isCollapsed = scrollOffset > 60
  const [settingsOpen, setSettingsOpen] = useState(false)
  const mobileMenuX = useRef(new Animated.Value(320)).current
  const { locale, changeLanguage } = useI18n()
  const { themeMode, setThemeMode } = useTheme()
  const { signOut, user, isAuthenticated, isAdmin } = useAuth()
  const { count: liveNotificationCount, markRead } = useNotificationCount(isAuthenticated)
  const isApprovedMember = isAuthenticated && user?.application_status === 'approved'
  const colors = useThemeColors()
  const drawerWidth = Math.min(width * 0.8, 320)
  const backdropColor = 'rgba(0,0,0,0.62)'
  const showCenteredPublicFloatingMenu = isDesktop && isCollapsed && !isAuthenticated
  const effectiveNotificationCount = isAuthenticated
    ? (notificationCount ?? liveNotificationCount)
    : 0
  const notificationBadgeText =
    effectiveNotificationCount > 99 ? '99+' : String(effectiveNotificationCount)

  const headerNavItems = useMemo(
    () => buildNavItems(isAuthenticated, isApprovedMember, isAdmin, t, false),
    [isAuthenticated, isApprovedMember, isAdmin, t]
  )
  const sideMenuNavItems = useMemo(
    () => buildNavItems(isAuthenticated, isApprovedMember, isAdmin, t, true),
    [isAuthenticated, isApprovedMember, isAdmin, t]
  )

  useEffect(() => {
    if (!settingsOpen) {
      mobileMenuX.setValue(drawerWidth)
    }
  }, [drawerWidth, settingsOpen, mobileMenuX])

  async function handleSignOut() {
    closeSettings()
    await signOut()
    router.replace('/')
  }

  function openSettings() {
    mobileMenuX.setValue(drawerWidth)
    setSettingsOpen(true)
    Animated.timing(mobileMenuX, {
      toValue: 0,
      duration: 220,
      useNativeDriver: Platform.OS !== 'web',
    }).start()
  }

  function toggleSettings() {
    if (settingsOpen) {
      closeSettings()
      return
    }
    openSettings()
  }

  function closeSettings() {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const active = document.activeElement
      if (active instanceof HTMLElement) active.blur()
    }
    Animated.timing(mobileMenuX, {
      toValue: drawerWidth,
      duration: 180,
      useNativeDriver: Platform.OS !== 'web',
    }).start(() => setSettingsOpen(false))
  }

  async function handleNotificationPress() {
    if (!isAuthenticated) return

    closeSettings()

    try {
      if (onNotificationPress) {
        onNotificationPress()
      } else {
        await markRead()
      }
    } finally {
      router.push('/community' as never)
    }
  }

  return (
    <View
      className={`${fixed ? 'absolute left-0 right-0' : ''} z-50`}
      style={{
        ...(fixed ? { top: 0, pointerEvents: 'box-none' as const } : {}),
        paddingTop: insets.top,
      }}
    >
      <View
        className={`rounded-full py-2 ${
          isCollapsed ? 'self-center w-[90%] lg:w-[94%] lg:max-w-[760px] shadow-lg' : 'self-stretch'
        }`}
        style={{
          backgroundColor: 'transparent',
          borderWidth: 0,
        }}
      >
        <View
          className={`flex-row items-center justify-between gap-3 web:backdrop-blur-xl ${
            isCollapsed ? 'rounded-full border px-2 py-1' : 'px-4 lg:px-0'
          }`}
          style={
            isCollapsed
              ? {
                  backgroundColor: transparent ? 'transparent' : `${colors.surface}EE`,
                  borderColor: 'transparent',
                }
              : undefined
          }
        >
          {showCenteredPublicFloatingMenu ? (
            <View className="hidden lg:flex w-full flex-row items-center justify-between px-2">
              <Pressable
                className="min-w-[74px]"
                onPress={() => router.push('/' as never)}
                accessibilityLabel={t('header.goHome')}
              >
                <TurbinaLogo width={isDesktop ? 70 : 60} showText={false} />
              </Pressable>

              <View className="flex-1 flex-row flex-wrap items-center justify-end gap-2">
                {headerNavItems.map((item) => {
                  const isActive = activeRoute === item.route
                  return (
                    <Link key={item.label} href={item.route as Href} asChild>
                      <Pressable
                        className="rounded-full px-4 py-2"
                        style={{ backgroundColor: isActive ? colors.primary : 'transparent' }}
                      >
                        <Text
                          className="text-xs font-medium uppercase tracking-[1.8px]"
                          style={{ color: isActive ? colors.white : colors.dark }}
                        >
                          {item.label}
                        </Text>
                      </Pressable>
                    </Link>
                  )
                })}
                <Pressable
                  className="rounded-full px-5 py-2"
                  style={{ backgroundColor: colors.primary }}
                  onPress={() => router.push('/auth?mode=signup' as never)}
                >
                  <Text
                    className="text-xs font-medium uppercase tracking-[1.8px]"
                    style={{ color: colors.white }}
                  >
                    {t('publicHeader.signUp')}
                  </Text>
                </Pressable>

                <Pressable
                  className="h-12 w-12 items-center justify-center"
                  onPress={toggleSettings}
                  accessibilityLabel={t('header.openMenu')}
                >
                  <Ionicons name="menu-outline" size={28} color={colors.dark} />
                </Pressable>
              </View>
            </View>
          ) : (
            <>
              <View className="hidden lg:flex flex-1 items-start justify-center">
                <Pressable
                  className="min-w-[74px] web:ml-6"
                  onPress={() => router.push('/' as never)}
                  accessibilityLabel={t('header.goHome')}
                >
                  <TurbinaLogo width={isDesktop ? 70 : 60} showText={!isCollapsed} />
                </Pressable>
              </View>

              {/* Desktop: nav items centered */}
              <View className="hidden lg:flex flex-row flex-1 items-center justify-center">
                {headerNavItems.map((item) => {
                  const isActive = activeRoute === item.route
                  return (
                    <Link key={item.label} href={item.route as Href} asChild>
                      <Pressable
                        className="rounded-full px-4 py-2"
                        style={{ backgroundColor: isActive ? colors.primary : 'transparent' }}
                      >
                        <Text
                          className="text-xs font-medium uppercase tracking-[1.8px]"
                          style={{ color: isActive ? colors.white : colors.dark }}
                        >
                          {item.label}
                        </Text>
                      </Pressable>
                    </Link>
                  )
                })}
              </View>

              <View className="hidden lg:flex flex-1 items-end justify-center">
                <View className="flex-row items-center gap-2 web:mr-6">
                  {!isAuthenticated && (
                    <Pressable
                      className="rounded-full px-5 py-2"
                      style={{ backgroundColor: colors.primary }}
                      onPress={() => router.push('/auth?mode=signup' as never)}
                    >
                      <Text
                        className="text-xs font-medium uppercase tracking-[1.8px]"
                        style={{ color: colors.white }}
                      >
                        {t('publicHeader.signUp')}
                      </Text>
                    </Pressable>
                  )}

                  {isAuthenticated && (
                    <Pressable
                      className="h-12 min-w-12 px-2 items-center justify-center rounded-full"
                      onPress={handleNotificationPress}
                      accessibilityLabel={t('header.notifications')}
                    >
                      <View className="relative h-6 w-6 items-center justify-center">
                        <Icon name="bell-o" size={18} color={colors.dark} />
                        {effectiveNotificationCount > 0 && (
                          <View
                            className="absolute -top-2 -right-3 min-w-[18px] h-[18px] px-1 rounded-full items-center justify-center"
                            style={{
                              backgroundColor: colors.primary,
                              borderWidth: 1,
                              borderColor: colors.background,
                            }}
                          >
                            <Text
                              className="text-[10px] font-medium"
                              style={{ color: colors.white }}
                            >
                              {notificationBadgeText}
                            </Text>
                          </View>
                        )}
                      </View>
                    </Pressable>
                  )}

                  <Pressable
                    className="h-12 w-12 items-center justify-center"
                    onPress={toggleSettings}
                    accessibilityLabel={t('header.openMenu')}
                  >
                    <Ionicons name="menu-outline" size={28} color={colors.dark} />
                  </Pressable>
                </View>
              </View>
            </>
          )}

          <View className="lg:hidden flex-row w-full items-center justify-between">
            <Pressable
              className="min-w-[74px]"
              onPress={() => router.push('/' as never)}
              accessibilityLabel={t('header.goHome')}
            >
              <TurbinaLogo width={isDesktop ? 70 : 60} showText />
            </Pressable>

            <View className="flex-row items-center gap-2">
              {isAuthenticated && (
                <Pressable
                  className="h-12 min-w-12 px-2 items-center justify-center rounded-full"
                  onPress={handleNotificationPress}
                  accessibilityLabel={t('header.notifications')}
                >
                  <View className="relative h-6 w-6 items-center justify-center">
                    <Icon name="bell-o" size={18} color={colors.dark} />
                    {effectiveNotificationCount > 0 && (
                      <View
                        className="absolute -top-2 -right-3 min-w-[18px] h-[18px] px-1 rounded-full items-center justify-center"
                        style={{
                          backgroundColor: colors.primary,
                          borderWidth: 1,
                          borderColor: colors.background,
                        }}
                      >
                        <Text className="text-[10px] font-medium" style={{ color: colors.white }}>
                          {notificationBadgeText}
                        </Text>
                      </View>
                    )}
                  </View>
                </Pressable>
              )}

              <Pressable
                className="h-12 w-12 items-center justify-center"
                onPress={toggleSettings}
                accessibilityLabel={t('header.openMenu')}
              >
                <Ionicons name="menu-outline" size={28} color={colors.dark} />
              </Pressable>
            </View>
          </View>
        </View>
      </View>

      {settingsOpen ? (
        Platform.OS === 'web' ? (
          <Pressable
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              backgroundColor: backdropColor,
            }}
            onPress={closeSettings}
          >
            <Animated.View
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: drawerWidth,
                height: '100%',
                backgroundColor: colors.background,
                paddingTop: insets.top,
                paddingHorizontal: 12,
                transform: [{ translateX: mobileMenuX }],
              }}
            >
              <Pressable onPress={(e) => e.stopPropagation()}>
                <SettingsContent
                  isApprovedMember={isApprovedMember}
                  isAuthenticated={isAuthenticated}
                  themeMode={themeMode}
                  setThemeMode={setThemeMode}
                  locale={locale}
                  changeLanguage={changeLanguage}
                  onSignOut={handleSignOut}
                  onClose={closeSettings}
                  navItems={sideMenuNavItems}
                  activeRoute={activeRoute}
                  onNavigate={(route) => router.push(route as never)}
                  showNav
                />
              </Pressable>
            </Animated.View>
          </Pressable>
        ) : (
          <Modal
            transparent
            visible={settingsOpen}
            onRequestClose={closeSettings}
            animationType="none"
          >
            <Pressable
              className="flex-1 items-end"
              style={{ backgroundColor: backdropColor }}
              onPress={closeSettings}
            >
              <Animated.View
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: drawerWidth,
                  height: '100%',
                  backgroundColor: colors.background,
                  paddingTop: insets.top,
                  paddingHorizontal: 12,
                  transform: [{ translateX: mobileMenuX }],
                }}
              >
                <Pressable onPress={(e) => e.stopPropagation()}>
                  <SettingsContent
                    isApprovedMember={isApprovedMember}
                    isAuthenticated={isAuthenticated}
                    themeMode={themeMode}
                    setThemeMode={setThemeMode}
                    locale={locale}
                    changeLanguage={changeLanguage}
                    onSignOut={handleSignOut}
                    onClose={closeSettings}
                    navItems={sideMenuNavItems}
                    activeRoute={activeRoute}
                    onNavigate={(route) => router.push(route as never)}
                    showNav
                  />
                </Pressable>
              </Animated.View>
            </Pressable>
          </Modal>
        )
      ) : null}
    </View>
  )
}
